import { createContext, useContext, useState, useCallback, useRef, useMemo, ReactNode } from 'react';
import defaults from './defaults.json';
import { mergeTranslations } from './mergeTranslations';

type Locale = 'en' | 'es';

interface I18nContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

interface I18nProviderProps {
  children: ReactNode;
  translations: Record<string, Record<string, unknown>>;
  defaultLocale?: Locale;
}

const I18nContext = createContext<I18nContextType | null>(null);

export function I18nProvider({ children, translations: translationsProp, defaultLocale = 'es' }: I18nProviderProps) {
  const mergedTranslations = useMemo(() => {
    const result: Record<string, Record<string, unknown>> = {};
    for (const locale of ['en', 'es'] as Locale[]) {
      const base = defaults as unknown as Record<string, unknown>;
      const tier = translationsProp[locale] as Record<string, unknown> | undefined;
      result[locale] = tier ? mergeTranslations(base, tier) : (base as Record<string, unknown>);
    }
    return result;
  }, [translationsProp]);

  const translationsRef = useRef(mergedTranslations);
  translationsRef.current = mergedTranslations;

  const [locale, setLocale] = useState<Locale>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('atlas-locale') as Locale;
      if (saved && translationsProp[saved]) return saved;
      const browserLang = navigator.language.split('-')[0] as Locale;
      if (translationsProp[browserLang]) return browserLang;
    }
    return defaultLocale;
  });

  const handleSetLocale = useCallback((newLocale: Locale) => {
    setLocale(newLocale);
    if (typeof window !== 'undefined') {
      localStorage.setItem('atlas-locale', newLocale);
    }
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      const translations = translationsRef.current;
      const keys = key.split('.');
      let value: unknown = translations[locale];

      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = (value as Record<string, unknown>)[k];
        } else {
          console.warn(`Translation key not found: ${key}`);
          return key;
        }
      }

      if (typeof value !== 'string') {
        console.warn(`Translation value is not a string: ${key}`);
        return key;
      }

      if (params) {
        return Object.entries(params).reduce(
          (acc, [paramKey, paramValue]) =>
            acc.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(paramValue)),
          value,
        );
      }

      return value;
    },
    [locale],
  );

  return (
    <I18nContext.Provider value={{ locale, setLocale: handleSetLocale, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}

export type { Locale, I18nContextType };
